import { findAndReplace } from "mdast-util-find-and-replace"

import type { DMDecoratedText, MentionMetadata } from "@/lib/client/generated/TypeDefinitions.ts"
import type { PhrasingContent, Root } from "mdast"
import type { RegExpMatchObject } from "mdast-util-find-and-replace"
import type { Plugin } from "unified"

type MentionOptions = {
  profileLink: (username: string) => string
  pathLink: (path: string) => string
}

export const AGENT_MENTION_TRIGGER = "@agent"
export const FILE_MENTION_TRIGGER = "@file:"

export const RE_FILE_MENTION_UNESCAPED_PATH = /[a-zA-Z0-9/.\-_]*/g
export const RE_FILE_MENTION_UNESCAPED_PATH_WITH_SPACE = /[a-zA-Z0-9/.\-_][a-zA-Z0-9/.\-_ ]*/g
export const RE_PROFILE_MENTION_USERNAME = /[a-zA-Z0-9][a-zA-Z0-9.\-_]*/g
export const RE_PROFILE_MENTION_USERNAME_WITH_SPACE = /[a-zA-Z0-9][a-zA-Z0-9.\-_]*( [a-zA-Z0-9.\-_]*)?/g
const RE_FILE_MENTION = /@file:((".*?")|('.*?')|([a-zA-Z0-9/.\-_]+))/g
const RE_PROFILE_MENTION = /@[a-zA-Z0-9][a-zA-Z0-9.\-_]*/g
export const RE_MENTION = new RegExp(`(${RE_FILE_MENTION.source}|${RE_PROFILE_MENTION.source})`, "g")

const FILE_URL_PREFIX = "/file/"
const PROFILE_URL_PREFIX = "/profile/"

export function isFileMention(url: string): boolean {
  return url.startsWith(FILE_URL_PREFIX)
}

export function isProfileMention(url: string): boolean {
  return url.startsWith(PROFILE_URL_PREFIX)
}

const quotesRegex = /^['"]|['"]$/g

export function isEmailMention(url: string): boolean {
  return url.startsWith("mailto:")
}

export function createMentionsPlugin(text: DMDecoratedText | string): Plugin<[MentionOptions], Root> {
  const metadata: MentionMetadata[] = []

  if (typeof text !== "string") {
    metadata.push(...text.metadata.mentions)
  }

  function replaceMention(...parameters: unknown[]): PhrasingContent[] | null {
    const value = parameters[0] as string
    const match = parameters[parameters.length - 1] as RegExpMatchObject

    let parentOffset = 0
    if (match.stack.length > 0) {
      const start = match.stack[match.stack.length - 1].position?.start.offset
      if (start) {
        parentOffset = start
      }
    }

    let meta: MentionMetadata | null = null

    for (const range of metadata) {
      if (range.fromIndex == parentOffset + match.index && range.toIndex == parentOffset + match.index + value.length - 1) {
        meta = range
        break
      }
    }

    if (!meta) {
      return [
        {
          type: "text",
          value: value
        }
      ]
    }

    if (value.startsWith(FILE_MENTION_TRIGGER)) {
      const path = value.substring(FILE_MENTION_TRIGGER.length).replace(quotesRegex, "")
      return [
        {
          type: "link",
          children: [
            {
              type: "text",
              value: path
            }
          ],
          url: `${FILE_URL_PREFIX}${path}`
        }
      ]
    } else {
      return [
        {
          type: "link",
          children: [
            {
              type: "text",
              value: value
            }
          ],
          url: `${PROFILE_URL_PREFIX}${value.substring(1)}`
        }
      ]
    }
  }

  return () => (tree: Root, _file) => {
    findAndReplace(tree, [[RE_MENTION, replaceMention]])
  }
}

export function decoratedText(text: string): DMDecoratedText {
  return {
    value: text,
    metadata: {
      mentions: []
    }
  }
}
